import type {
  GraphMember,
  Relationship,
  Side,
} from '@/contexts/FamilyGraphContext';

import {
  filterMembersByBranchScope,
} from '@/lib/lineage';

import {
  projectCanonicalTreeCompatibilityEdges,
} from './CanonicalTreeCompatibilityEdgeProjection';

import type {
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

import {
  projectCanonicalTreeForViewer,
  type CanonicalTreeViewerProjectionResult,
  type CanonicalViewerMemberProjection,
} from './CanonicalTreeViewerProjection';

import type {
  CanonicalTreeGeometryRole,
} from './CanonicalTreeGeometryRoleAuthority';

import type {
  CanonicalTreeGeometryBranchRoot,
} from './CanonicalTreeGeometryBranchRootAuthority';

import {
  resolveCanonicalTreeVisualClusterClass,
  resolveCanonicalTreeVisualClusterKey,
} from './CanonicalTreeVisualPresentationAuthority';

export interface CanonicalTreeViewerPresentationInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerId:
    string | null | undefined;

  readonly centeredMemberId?:
    string | null | undefined;

  /**
   * Optional preassembled canonical evidence.
   *
   * Production may use compatibility projection. Deterministic fixtures
   * may provide an exact governed edge set.
   */
  readonly edges?:
    readonly CanonicalTreeRelationshipEdge[];
}

export interface CanonicalTreeViewerPresentationResult {
  /**
   * Every branch-authorized person canonically reachable from the viewer.
   *
   * Bloodline and modern-family connections are equally Tree-visible.
   */
  readonly treeMembers:
    readonly GraphMember[];

  /**
   * Every branch-authorized saved person.
   *
   * Canonically disconnected rows remain available as Needs connection.
   */
  readonly rosterMembers:
    readonly GraphMember[];

  readonly reachableMemberIds:
    ReadonlySet<string>;

  readonly disconnectedMemberIds:
    ReadonlySet<string>;

  readonly projection:
    CanonicalTreeViewerProjectionResult | null;

  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];

  readonly unresolvedEvidenceCount:
    number;

  readonly conflictingEvidenceCount:
    number;

  readonly invalidCompatibilityEvidenceCount:
    number;

  readonly mutatesCanonicalMembers:
    false;
}

type PresentationFields = {
  readonly relationship:
    Relationship;

  readonly side:
    Side;

  readonly relationshipOverride:
    string | null;
};

const defaultModernFamilyTitle = (
  projection:
    CanonicalViewerMemberProjection,
): string | null => {
  switch (projection.connectionKind) {
    case 'co-parent':
      return 'Co-Parent';

    case 'step-family':
      return 'Step Family';

    case 'in-law':
      return 'In-Law';

    case 'guardian':
      return 'Guardian';

    case 'foster-family':
      return 'Foster Family';

    case 'chosen-family':
      return 'Chosen Family';

    case 'god-family':
      return 'God Family';

    case 'affiliated-family':
      return 'Affiliated Family';

    default:
      return null;
  }
};

const preferredTitleFor = (
  projection:
    CanonicalViewerMemberProjection,

  edgeById:
    ReadonlyMap<
      string,
      CanonicalTreeRelationshipEdge
    >,
): string | null => {
  for (
    const edgeId of
    projection.sourceEdgeIds
  ) {
    const title =
      edgeById.get(edgeId)
        ?.preferredFamilyTitle
        ?.trim();

    if (title) return title;
  }

  return defaultModernFamilyTitle(
    projection,
  );
};

const ancestorRelationship = (
  member: GraphMember,
  projection:
    CanonicalViewerMemberProjection,
): Relationship => {
  const generation =
    projection.generation ?? 0;

  if (generation === 1) {
    if (
      member.relationship === 'mother' ||
      member.relationship === 'father' ||
      member.relationship === 'step-parent' ||
      member.relationship === 'adopted-parent'
    ) {
      return member.relationship;
    }

    if (projection.side === 'mother') {
      return 'mother';
    }

    if (projection.side === 'father') {
      return 'father';
    }

    return 'unknown';
  }

  if (generation === 2) {
    return 'grandparent';
  }

  return 'great-grandparent';
};

const presentationFieldsFor = (
  member: GraphMember,
  projection:
    CanonicalViewerMemberProjection,

  edgeById:
    ReadonlyMap<
      string,
      CanonicalTreeRelationshipEdge
    >,
): PresentationFields => {
  switch (projection.connectionKind) {
    case 'self':
      return {
        relationship:
          'unknown',
        side:
          'self',
        relationshipOverride:
          null,
      };

    case 'ancestor':
      return {
        relationship:
          ancestorRelationship(
            member,
            projection,
          ),
        side:
          projection.side,
        relationshipOverride:
          member.relationshipOverride ??
          null,
      };

    case 'descendant':
      /*
       * CC-TREE-LIVE-003G2X
       *
       * A terminal downward parent-child edge does not make every person
       * a viewer descendant.
       *
       * Collateral bloodline paths such as:
       *
       *   viewer -> mother -> grandparents -> aunt
       *   viewer -> father -> grandparents -> uncle -> cousin
       *   viewer -> one parent -> half-sibling
       *
       * legitimately end in a downward edge while remaining owned by a
       * canonical maternal, paternal, or both-side branch.
       *
       * CanonicalTreeViewerProjection now classifies viewer-relative
       * kinship from the complete structural path. Preserve that branch
       * and kinship here instead of collapsing them to self/cousin-child.
       */
      if (
        projection.branch === 'maternal' ||
        projection.branch === 'paternal' ||
        projection.branch === 'both'
      ) {
        return {
          relationship:
            projection.kinshipRelationship,
          side:
            projection.side,
          relationshipOverride:
            member.relationshipOverride ??
            null,
        };
      }

      /*
       * True viewer descendants retain the established self presentation
       * authority. This deliberately does not change spouse/co-parent/
       * modern-family presentation paths.
       */
      return {
        relationship:
          (projection.generation ?? 0) === -1
            ? 'child'
            : 'cousin-child',
        side:
          'self',
        relationshipOverride:
          member.relationshipOverride ??
          null,
      };

    case 'sibling':
      return {
        relationship:
          'sibling',
        side:
          projection.side,
        relationshipOverride:
          member.relationshipOverride ??
          null,
      };

    case 'spouse-partner':
      return {
        relationship:
          'spouse',
        side:
          'self',
        relationshipOverride:
          preferredTitleFor(
            projection,
            edgeById,
          ) ??
          member.relationshipOverride ??
          null,
      };

    /*
     * The legacy Relationship enum has no dedicated values for these
     * modern-family relationships.
     *
     * Their canonical edges make them Tree-connected. The presentation row
     * therefore remains topology-neutral while preserving a truthful title.
     */
    case 'co-parent':
    case 'step-family':
    case 'in-law':
    case 'guardian':
    case 'foster-family':
    case 'chosen-family':
    case 'god-family':
    case 'affiliated-family':
      return {
        relationship:
          'unknown',
        side:
          'self',
        relationshipOverride:
          preferredTitleFor(
            projection,
            edgeById,
          ) ??
          member.relationshipOverride ??
          null,
      };

    case 'unknown':
    default:
      return {
        relationship:
          member.relationship,
        side:
          member.side,
        relationshipOverride:
          member.relationshipOverride ??
          null,
      };
  }
};

const adaptMember = (
  member: GraphMember,
  projection:
    CanonicalViewerMemberProjection | undefined,

  edgeById:
    ReadonlyMap<
      string,
      CanonicalTreeRelationshipEdge
    >,
): GraphMember => {
  if (!projection) return member;

  const presentation =
    presentationFieldsFor(
      member,
      projection,
      edgeById,
    );

  return {
    ...member,
    relationship:
      presentation.relationship,
    side:
      presentation.side,
    relationshipOverride:
      presentation.relationshipOverride,

    /*
     * Structural and identity authority is copied unchanged.
     */
    parentIds:
      [...(member.parentIds ?? [])],
    spouseId:
      member.spouseId ?? null,
    relationshipWorkflow:
      member.relationshipWorkflow ?? null,
  };
};


export type CanonicalTreePresentationViewMode =
  | 'full'
  | 'mother'
  | 'father'
  | 'extended';

export interface CanonicalTreeTopologyClosedViewEdge {
  readonly key: string;
  readonly fromMemberIds:
    readonly string[];
  readonly toMemberId: string;
}

export interface CanonicalTreeTopologyClosedViewSpouseLink {
  readonly aMemberId: string;
  readonly bMemberId: string;
}

export interface CanonicalTreeTopologyClosedViewProjectionInput {
  readonly viewMode:
    CanonicalTreePresentationViewMode;

  readonly viewerMemberId:
    string | null | undefined;

  readonly cardMemberIds:
    readonly string[];

  readonly roleByMemberId:
    ReadonlyMap<
      string,
      CanonicalTreeGeometryRole
    >;

  readonly edges:
    readonly CanonicalTreeTopologyClosedViewEdge[];

  readonly spouseLinks:
    readonly CanonicalTreeTopologyClosedViewSpouseLink[];
}

export interface CanonicalTreeTopologyClosedViewProjectionResult {
  readonly viewMode:
    CanonicalTreePresentationViewMode;

  readonly selectedMemberIds:
    ReadonlySet<string>;

  readonly visibleMemberIds:
    ReadonlySet<string>;

  readonly bridgeMemberIds:
    ReadonlySet<string>;

  readonly unresolvedSelectedMemberIds:
    readonly string[];

  readonly usesCanonicalFullWorldBounds:
    true;

  readonly mutatesCanonicalGeometry:
    false;
}

const canonicalBloodlineKinship =
  new Set<Relationship>([
    'mother',
    'father',
    'sibling',
    'grandparent',
    'great-grandparent',
    'aunt-uncle',
    'cousin',
    'cousin-child',
  ]);

const canonicalExtendedKinship =
  new Set<Relationship>([
    'great-grandparent',
    'aunt-uncle',
    'cousin',
    'cousin-child',
  ]);

const roleIsBloodline = (
  role:
    CanonicalTreeGeometryRole | undefined,
): boolean =>
  !!role &&
  canonicalBloodlineKinship.has(
    role.kinshipRelationship,
  );

const isSelectedByView = (
  mode:
    CanonicalTreePresentationViewMode,
  memberId: string,
  viewerMemberId:
    string | null | undefined,
  role:
    CanonicalTreeGeometryRole | undefined,
): boolean => {
  if (
    viewerMemberId &&
    memberId === viewerMemberId
  ) {
    return true;
  }

  if (mode === 'full') {
    return true;
  }

  if (!role) {
    return false;
  }

  if (mode === 'mother') {
    return (
      roleIsBloodline(role) &&
      (
        role.branch === 'maternal' ||
        role.branch === 'both'
      )
    );
  }

  if (mode === 'father') {
    return (
      roleIsBloodline(role) &&
      (
        role.branch === 'paternal' ||
        role.branch === 'both'
      )
    );
  }

  return (
    roleIsBloodline(role) &&
    canonicalExtendedKinship.has(
      role.kinshipRelationship,
    )
  );
};

type ViewConnector = {
  readonly endpointMemberIds:
    readonly string[];
};

type ViewNeighbor = {
  readonly neighborMemberId:
    string;

  readonly connector:
    ViewConnector;
};

export const resolveCanonicalTreeTopologyClosedViewProjection = (
  input:
    CanonicalTreeTopologyClosedViewProjectionInput,
): CanonicalTreeTopologyClosedViewProjectionResult => {
  const cardMemberIds =
    Array.from(
      new Set(
        input.cardMemberIds
          .filter(Boolean),
      ),
    );

  const cardMemberIdSet =
    new Set(cardMemberIds);

  if (input.viewMode === 'full') {
    const all =
      new Set(cardMemberIds);

    return Object.freeze({
      viewMode:
        input.viewMode,
      selectedMemberIds:
        all,
      visibleMemberIds:
        new Set(all),
      bridgeMemberIds:
        new Set<string>(),
      unresolvedSelectedMemberIds:
        Object.freeze([]),
      usesCanonicalFullWorldBounds:
        true,
      mutatesCanonicalGeometry:
        false,
    });
  }

  const selectedMemberIds =
    new Set<string>();

  for (const memberId of cardMemberIds) {
    if (
      isSelectedByView(
        input.viewMode,
        memberId,
        input.viewerMemberId,
        input.roleByMemberId.get(
          memberId,
        ),
      )
    ) {
      selectedMemberIds.add(
        memberId,
      );
    }
  }

  if (
    !input.viewerMemberId ||
    !cardMemberIdSet.has(
      input.viewerMemberId,
    )
  ) {
    return Object.freeze({
      viewMode:
        input.viewMode,
      selectedMemberIds,
      visibleMemberIds:
        new Set<string>(),
      bridgeMemberIds:
        new Set<string>(),
      unresolvedSelectedMemberIds:
        Object.freeze(
          [...selectedMemberIds].sort(),
        ),
      usesCanonicalFullWorldBounds:
        true,
      mutatesCanonicalGeometry:
        false,
    });
  }

  const traversableMemberIds =
    new Set<string>([
      input.viewerMemberId,
    ]);

  for (const memberId of cardMemberIds) {
    const role =
      input.roleByMemberId.get(
        memberId,
      );

    if (!roleIsBloodline(role)) {
      continue;
    }

    if (input.viewMode === 'mother') {
      if (
        role?.branch === 'maternal' ||
        role?.branch === 'both'
      ) {
        traversableMemberIds.add(
          memberId,
        );
      }

      continue;
    }

    if (input.viewMode === 'father') {
      if (
        role?.branch === 'paternal' ||
        role?.branch === 'both'
      ) {
        traversableMemberIds.add(
          memberId,
        );
      }

      continue;
    }

    /*
     * Extended view may span both canonical bloodline branches.
     * Direct ancestors/siblings may therefore serve as bridge authority
     * without themselves becoming Extended semantic seeds.
     */
    traversableMemberIds.add(
      memberId,
    );
  }

  const connectors:
    ViewConnector[] = [];

  for (const edge of input.edges) {
    const endpointMemberIds =
      Array.from(
        new Set([
          ...edge.fromMemberIds,
          edge.toMemberId,
        ]),
      );

    if (
      endpointMemberIds.length < 2 ||
      endpointMemberIds.some(
        memberId =>
          !cardMemberIdSet.has(
            memberId,
          ),
      )
    ) {
      continue;
    }

    connectors.push(
      Object.freeze({
        endpointMemberIds:
          Object.freeze(
            endpointMemberIds,
          ),
      }),
    );
  }

  for (
    const link of
    input.spouseLinks
  ) {
    const endpointMemberIds =
      Array.from(
        new Set([
          link.aMemberId,
          link.bMemberId,
        ]),
      );

    if (
      endpointMemberIds.length !== 2 ||
      endpointMemberIds.some(
        memberId =>
          !cardMemberIdSet.has(
            memberId,
          ),
      )
    ) {
      continue;
    }

    connectors.push(
      Object.freeze({
        endpointMemberIds:
          Object.freeze(
            endpointMemberIds,
          ),
      }),
    );
  }

  const adjacency =
    new Map<
      string,
      ViewNeighbor[]
    >();

  const addNeighbor = (
    fromMemberId: string,
    neighbor:
      ViewNeighbor,
  ): void => {
    const values =
      adjacency.get(
        fromMemberId,
      ) ?? [];

    values.push(
      neighbor,
    );

    adjacency.set(
      fromMemberId,
      values,
    );
  };

  for (const connector of connectors) {
    const traversableEndpoints =
      connector.endpointMemberIds
        .filter(
          memberId =>
            traversableMemberIds.has(
              memberId,
            ),
        );

    for (
      let leftIndex = 0;
      leftIndex <
        traversableEndpoints.length;
      leftIndex += 1
    ) {
      for (
        let rightIndex =
          leftIndex + 1;
        rightIndex <
          traversableEndpoints.length;
        rightIndex += 1
      ) {
        const left =
          traversableEndpoints[
            leftIndex
          ];

        const right =
          traversableEndpoints[
            rightIndex
          ];

        addNeighbor(
          left,
          Object.freeze({
            neighborMemberId:
              right,
            connector,
          }),
        );

        addNeighbor(
          right,
          Object.freeze({
            neighborMemberId:
              left,
            connector,
          }),
        );
      }
    }
  }

  const distanceToViewer =
    new Map<string, number>([
      [
        input.viewerMemberId,
        0,
      ],
    ]);

  const queue: string[] = [
    input.viewerMemberId,
  ];

  while (queue.length > 0) {
    const current =
      queue.shift()!;

    const currentDistance =
      distanceToViewer.get(
        current,
      )!;

    for (
      const neighbor of
      adjacency.get(
        current,
      ) ?? []
    ) {
      if (
        distanceToViewer.has(
          neighbor.neighborMemberId,
        )
      ) {
        continue;
      }

      distanceToViewer.set(
        neighbor.neighborMemberId,
        currentDistance + 1,
      );

      queue.push(
        neighbor.neighborMemberId,
      );
    }
  }

  const visibleMemberIds =
    new Set<string>([
      input.viewerMemberId,
    ]);

  const unresolvedSelectedMemberIds:
    string[] = [];

  const walkSeed = (
    seedMemberId: string,
  ): void => {
    const initialDistance =
      distanceToViewer.get(
        seedMemberId,
      );

    if (
      initialDistance === undefined
    ) {
      unresolvedSelectedMemberIds.push(
        seedMemberId,
      );
      return;
    }

    const visited =
      new Set<string>();

    const visit = (
      memberId: string,
    ): void => {
      if (visited.has(memberId)) {
        return;
      }

      visited.add(memberId);
      visibleMemberIds.add(
        memberId,
      );

      const distance =
        distanceToViewer.get(
          memberId,
        );

      if (
        distance === undefined ||
        distance === 0
      ) {
        return;
      }

      const next =
        (
          adjacency.get(
            memberId,
          ) ?? []
        ).filter(
          neighbor =>
            distanceToViewer.get(
              neighbor.neighborMemberId,
            ) ===
              distance - 1,
        );

      for (const transition of next) {
        /*
         * Layout parent connectors are hyperedges: a routed edge may own
         * two parent endpoints plus one child endpoint. If that canonical
         * connector is required by a shortest path, every endpoint must
         * remain visible or the connector would be fabricated/partial.
         */
        for (
          const endpointMemberId of
          transition.connector
            .endpointMemberIds
        ) {
          visibleMemberIds.add(
            endpointMemberId,
          );
        }

        visit(
          transition
            .neighborMemberId,
        );
      }
    };

    visit(seedMemberId);
  };

  for (
    const seedMemberId of
    selectedMemberIds
  ) {
    walkSeed(
      seedMemberId,
    );
  }

  const bridgeMemberIds =
    new Set(
      [...visibleMemberIds]
        .filter(
          memberId =>
            !selectedMemberIds.has(
              memberId,
            ),
        ),
    );

  return Object.freeze({
    viewMode:
      input.viewMode,
    selectedMemberIds,
    visibleMemberIds,
    bridgeMemberIds,
    unresolvedSelectedMemberIds:
      Object.freeze(
        Array.from(
          new Set(
            unresolvedSelectedMemberIds,
          ),
        ).sort(),
      ),
    usesCanonicalFullWorldBounds:
      true,
    mutatesCanonicalGeometry:
      false,
  });
};

export const resolveCanonicalTreeViewerPresentation = (
  input:
    CanonicalTreeViewerPresentationInput,
): CanonicalTreeViewerPresentationResult => {
  const members =
    [...input.members];

  const centeredMemberId =
    input.centeredMemberId ??
    input.viewerId ??
    null;

  const branchScopedMembers =
    filterMembersByBranchScope(
      members,
      centeredMemberId,
    );

  if (
    !input.viewerId ||
    !members.some(
      member =>
        member.id === input.viewerId,
    )
  ) {
    const reachableMemberIds =
      new Set(
        branchScopedMembers.map(
          member => member.id,
        ),
      );

    return Object.freeze({
      treeMembers:
        Object.freeze([
          ...branchScopedMembers,
        ]),
      rosterMembers:
        Object.freeze([
          ...branchScopedMembers,
        ]),
      reachableMemberIds,
      disconnectedMemberIds:
        new Set<string>(),
      projection:
        null,
      edges:
        Object.freeze([]),
      unresolvedEvidenceCount:
        1,
      conflictingEvidenceCount:
        0,
      invalidCompatibilityEvidenceCount:
        0,
      mutatesCanonicalMembers:
        false,
    });
  }

  const compatibility =
    input.edges
      ? null
      : projectCanonicalTreeCompatibilityEdges({
          members,
        });

  const edges =
    input.edges
      ? [...input.edges]
      : [
          ...(compatibility?.edges ?? []),
        ];

  const projection =
    projectCanonicalTreeForViewer({
      viewerId:
        input.viewerId,
      members,
      edges,
    });

  const edgeById =
    new Map(
      edges.map(
        edge =>
          [edge.id, edge] as const,
      ),
    );

  const branchScopedIdSet =
    new Set(
      branchScopedMembers.map(
        member => member.id,
      ),
    );

  const reachableMemberIds =
    new Set(
      Array.from(
        projection.projections.keys(),
      ).filter(
        memberId =>
          branchScopedIdSet.has(memberId),
      ),
    );

  const disconnectedMemberIds =
    new Set(
      branchScopedMembers
        .map(member => member.id)
        .filter(
          memberId =>
            !reachableMemberIds.has(
              memberId,
            ),
        ),
    );

  const rosterMembers =
    branchScopedMembers.map(
      member =>
        adaptMember(
          member,
          projection.projections.get(
            member.id,
          ),
          edgeById,
        ),
    );

  const treeMembers =
    rosterMembers.filter(
      member =>
        reachableMemberIds.has(
          member.id,
        ),
    );

  return Object.freeze({
    treeMembers:
      Object.freeze(treeMembers),
    rosterMembers:
      Object.freeze(rosterMembers),
    reachableMemberIds,
    disconnectedMemberIds,
    projection,
    edges:
      Object.freeze([
        ...edges,
      ]),
    unresolvedEvidenceCount:
      (
        compatibility
          ?.unresolvedEvidence.length ??
        0
      ) +
      projection
        .unresolvedEvidence.length,
    conflictingEvidenceCount:
      projection
        .conflictingEvidence.length,
    invalidCompatibilityEvidenceCount:
      compatibility
        ?.invalidEvidence.length ??
      0,
    mutatesCanonicalMembers:
      false,
  });
};

/*
 * CC-TREE-LIVE-003G3S
 *
 * Visible family-cluster palette allocation is presentation-only.
 *
 * 003G3R remains canonical authority for:
 *
 * - structural family-cluster normalization;
 * - root-level v0 compatibility;
 * - historical preferred family variant.
 *
 * 003G3S adds only set-level collision avoidance across the
 * simultaneously visible canonical family-cluster set.
 */

export type CanonicalTreeVisibleClusterPaletteClass =
  | 'kc-tree-cluster-v0'
  | 'kc-tree-cluster-v1'
  | 'kc-tree-cluster-v2'
  | 'kc-tree-cluster-v3'
  | 'kc-tree-cluster-v4'
  | 'kc-tree-cluster-v5'
  | 'kc-tree-cluster-v6'
  | 'kc-tree-cluster-v7'
  | 'kc-tree-cluster-v8'
  | 'kc-tree-cluster-v9'
  | 'kc-tree-cluster-v10'
  | 'kc-tree-cluster-v11'
  | 'kc-tree-cluster-v12';

export type CanonicalTreeVisibleFamilyPaletteClass =
  Exclude<
    CanonicalTreeVisibleClusterPaletteClass,
    'kc-tree-cluster-v0'
  >;

export const CANONICAL_TREE_VISIBLE_FAMILY_PALETTE_CAPACITY =
  12 as const;

export const CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS:
  readonly CanonicalTreeVisibleFamilyPaletteClass[] =
  Object.freeze([
    'kc-tree-cluster-v1',
    'kc-tree-cluster-v2',
    'kc-tree-cluster-v3',
    'kc-tree-cluster-v4',
    'kc-tree-cluster-v5',
    'kc-tree-cluster-v6',
    'kc-tree-cluster-v7',
    'kc-tree-cluster-v8',
    'kc-tree-cluster-v9',
    'kc-tree-cluster-v10',
    'kc-tree-cluster-v11',
    'kc-tree-cluster-v12',
  ]);

export interface CanonicalTreeVisibleClusterPaletteCandidate {
  readonly geometryRoot:
    CanonicalTreeGeometryBranchRoot;

  readonly clusterKey:
    string | null | undefined;
}

export interface CanonicalTreeVisibleClusterPaletteResult {
  readonly allocationByClusterIdentity:
    ReadonlyMap<
      string,
      CanonicalTreeVisibleFamilyPaletteClass
    >;

  readonly familyClusterCount:
    number;

  readonly allocatedFamilyClusterCount:
    number;

  readonly rootLevelClusterCount:
    number;

  readonly capacity:
    typeof CANONICAL_TREE_VISIBLE_FAMILY_PALETTE_CAPACITY;

  readonly capacityExceeded:
    boolean;

  readonly preferredVariantReuseCount:
    number;

  readonly collisionResolutionCount:
    number;

  readonly unallocatedClusterIdentities:
    readonly string[];

  readonly mutatesCanonicalGeometry:
    false;

  readonly mutatesCanonicalTopology:
    false;

  readonly grantsMutationAuthority:
    false;
}

const countNumericPathTokens = (
  canonicalClusterKey: string,
): number =>
  canonicalClusterKey
    .split('.')
    .slice(1)
    .filter(
      token =>
        /^\d+$/.test(token),
    )
    .length;

const isCanonicalFamilyClusterKey = (
  canonicalClusterKey: string,
): boolean =>
  countNumericPathTokens(
    canonicalClusterKey,
  ) >= 2;

export const resolveCanonicalTreeVisibleClusterIdentity = (
  root:
    CanonicalTreeGeometryBranchRoot,
  clusterKey:
    string | null | undefined,
): string | null => {
  const canonicalClusterKey =
    resolveCanonicalTreeVisualClusterKey(
      root,
      clusterKey,
    );

  if (!canonicalClusterKey) {
    return null;
  }

  return [
    root,
    canonicalClusterKey,
  ].join(':');
};

export const allocateCanonicalTreeVisibleClusterPalette = (
  candidates:
    readonly CanonicalTreeVisibleClusterPaletteCandidate[],
): CanonicalTreeVisibleClusterPaletteResult => {
  const familyByIdentity =
    new Map<
      string,
      {
        readonly preferredClass:
          CanonicalTreeVisibleFamilyPaletteClass;
      }
    >();

  const rootLevelIdentities =
    new Set<string>();

  for (const candidate of candidates) {
    const canonicalClusterKey =
      resolveCanonicalTreeVisualClusterKey(
        candidate.geometryRoot,
        candidate.clusterKey,
      );

    if (!canonicalClusterKey) {
      continue;
    }

    const identity =
      resolveCanonicalTreeVisibleClusterIdentity(
        candidate.geometryRoot,
        candidate.clusterKey,
      );

    if (!identity) {
      continue;
    }

    const historicalClass =
      resolveCanonicalTreeVisualClusterClass(
        candidate.geometryRoot,
        candidate.clusterKey,
      );

    if (
      historicalClass ===
      'kc-tree-cluster-v0'
    ) {
      rootLevelIdentities.add(
        identity,
      );
      continue;
    }

    if (
      !historicalClass ||
      !isCanonicalFamilyClusterKey(
        canonicalClusterKey,
      )
    ) {
      continue;
    }

    if (
      familyByIdentity.has(
        identity,
      )
    ) {
      continue;
    }

    familyByIdentity.set(
      identity,
      {
        preferredClass:
          historicalClass as
            CanonicalTreeVisibleFamilyPaletteClass,
      },
    );
  }

  /*
   * Ordering is structural and deterministic.
   *
   * No member name, member id, coordinate, relationship label,
   * visual color, or iteration order participates.
   */
  const orderedIdentities =
    Array.from(
      familyByIdentity.keys(),
    ).sort();

  const allocations =
    new Map<
      string,
      CanonicalTreeVisibleFamilyPaletteClass
    >();

  const usedVariants =
    new Set<
      CanonicalTreeVisibleFamilyPaletteClass
    >();

  const unallocated:
    string[] =
    [];

  let preferredVariantReuseCount =
    0;

  let collisionResolutionCount =
    0;

  for (
    const identity of
    orderedIdentities
  ) {
    if (
      allocations.size >=
      CANONICAL_TREE_VISIBLE_FAMILY_PALETTE_CAPACITY
    ) {
      unallocated.push(
        identity,
      );
      continue;
    }

    const evidence =
      familyByIdentity.get(
        identity,
      );

    if (!evidence) {
      continue;
    }

    const preferredIndex =
      Math.max(
        0,
        CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS.indexOf(
          evidence.preferredClass,
        ),
      );

    let selected:
      CanonicalTreeVisibleFamilyPaletteClass | null =
      null;

    for (
      let offset = 0;
      offset <
      CANONICAL_TREE_VISIBLE_FAMILY_PALETTE_CAPACITY;
      offset += 1
    ) {
      const candidateClass =
        CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS[
          (
            preferredIndex +
            offset
          ) %
          CANONICAL_TREE_VISIBLE_FAMILY_PALETTE_CAPACITY
        ];

      if (
        !usedVariants.has(
          candidateClass,
        )
      ) {
        selected =
          candidateClass;
        break;
      }
    }

    if (!selected) {
      /*
       * Fail closed. Never reuse a family variant merely
       * to force an allocation.
       */
      unallocated.push(
        identity,
      );
      continue;
    }

    allocations.set(
      identity,
      selected,
    );

    usedVariants.add(
      selected,
    );

    if (
      selected ===
      evidence.preferredClass
    ) {
      preferredVariantReuseCount +=
        1;
    } else {
      collisionResolutionCount +=
        1;
    }
  }

  return Object.freeze({
    allocationByClusterIdentity:
      allocations,

    familyClusterCount:
      orderedIdentities.length,

    allocatedFamilyClusterCount:
      allocations.size,

    rootLevelClusterCount:
      rootLevelIdentities.size,

    capacity:
      CANONICAL_TREE_VISIBLE_FAMILY_PALETTE_CAPACITY,

    capacityExceeded:
      unallocated.length > 0,

    preferredVariantReuseCount,

    collisionResolutionCount,

    unallocatedClusterIdentities:
      Object.freeze([
        ...unallocated,
      ]),

    mutatesCanonicalGeometry:
      false as const,

    mutatesCanonicalTopology:
      false as const,

    grantsMutationAuthority:
      false as const,
  });
};

export const resolveCanonicalTreeVisibleClusterPaletteClass = (
  allocation:
    CanonicalTreeVisibleClusterPaletteResult,
  root:
    CanonicalTreeGeometryBranchRoot,
  clusterKey:
    string | null | undefined,
): CanonicalTreeVisibleClusterPaletteClass | null => {
  const historicalClass =
    resolveCanonicalTreeVisualClusterClass(
      root,
      clusterKey,
    );

  if (!historicalClass) {
    return null;
  }

  /*
   * Preserve the exact 003G3R root-level compatibility class.
   */
  if (
    historicalClass ===
    'kc-tree-cluster-v0'
  ) {
    return historicalClass;
  }

  const identity =
    resolveCanonicalTreeVisibleClusterIdentity(
      root,
      clusterKey,
    );

  if (!identity) {
    return null;
  }

  /*
   * A visible family not present in the governed allocation,
   * including capacity overflow, fails closed to no family
   * variant rather than silently reusing another family's color.
   */
  return (
    allocation
      .allocationByClusterIdentity
      .get(identity) ??
    null
  );
};


/*
 * CC-TREE-LIVE-003G3T — canonical family-unit identity
 *
 * Identity authority is derived exclusively from governed directed
 * parent-child topology.
 *
 * Presentation, geometry, names, relationship labels, CSS, coordinates,
 * and palette classes do not participate.
 */

export type CanonicalTreeFamilyUnitIdentityStatus =
  | 'CERTIFIED'
  | 'UNCERTIFIED_ZERO_PARENT'
  | 'BLOCKED_UNRESOLVED_PARENT'
  | 'BLOCKED_INVALID_PARENT';

export interface CanonicalTreeFamilyUnitEvidence {
  readonly source:
    CanonicalTreeRelationshipEdge['source'];

  readonly sourceReferenceId:
    string | null;

  readonly memberIds:
    readonly string[];

  readonly reason:
    string;
}

export interface CanonicalTreeFamilyUnitMemberIdentity {
  readonly memberId:
    string;

  readonly status:
    CanonicalTreeFamilyUnitIdentityStatus;

  readonly familyUnitId:
    string | null;

  readonly canonicalParentMemberIds:
    readonly string[];
}

export interface CanonicalTreeFamilyUnitIdentityProjectionInput {
  readonly memberIds:
    readonly string[];

  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];

  readonly unresolvedEvidence:
    readonly CanonicalTreeFamilyUnitEvidence[];

  readonly invalidEvidence:
    readonly CanonicalTreeFamilyUnitEvidence[];
}

export interface CanonicalTreeFamilyUnitIdentityProjection {
  readonly identityByMemberId:
    ReadonlyMap<
      string,
      CanonicalTreeFamilyUnitMemberIdentity
    >;

  readonly mutatesCanonicalTopology:
    false;

  readonly mutatesCanonicalMembers:
    false;

  readonly grantsMutationAuthority:
    false;
}

const canonicalTreeFamilyUnitParentEdgeEligible = (
  edge:
    CanonicalTreeRelationshipEdge,
): boolean =>
  edge.kind === 'parent-child' &&
  edge.direction === 'directed' &&
  (
    edge.status === 'active' ||
    edge.status === 'not-applicable'
  ) &&
  edge.confirmation !== 'rejected';

const canonicalTreeFamilyUnitParentEvidenceBlocks = (
  memberId: string,
  evidence:
    readonly CanonicalTreeFamilyUnitEvidence[],
): boolean =>
  evidence.some(
    item =>
      item.source === 'parent_ids' &&
      item.sourceReferenceId ===
        memberId,
  );

const canonicalTreeFamilyUnitId = (
  parentMemberIds:
    readonly string[],
): string =>
  `parents:${parentMemberIds.join('|')}`;

export const resolveCanonicalTreeFamilyUnitIdentityProjection = (
  input:
    CanonicalTreeFamilyUnitIdentityProjectionInput,
): CanonicalTreeFamilyUnitIdentityProjection => {
  const memberIds =
    Array.from(
      new Set(
        input.memberIds
          .filter(Boolean),
      ),
    ).sort();

  const parentsByChild =
    new Map<
      string,
      Set<string>
    >();

  for (const edge of input.edges) {
    if (
      !canonicalTreeFamilyUnitParentEdgeEligible(
        edge,
      )
    ) {
      continue;
    }

    if (
      !parentsByChild.has(
        edge.targetMemberId,
      )
    ) {
      parentsByChild.set(
        edge.targetMemberId,
        new Set<string>(),
      );
    }

    parentsByChild
      .get(
        edge.targetMemberId,
      )!
      .add(
        edge.sourceMemberId,
      );
  }

  const identityByMemberId =
    new Map<
      string,
      CanonicalTreeFamilyUnitMemberIdentity
    >();

  for (const memberId of memberIds) {
    const canonicalParentMemberIds =
      Object.freeze(
        Array.from(
          parentsByChild.get(
            memberId,
          ) ??
          new Set<string>(),
        ).sort(),
      );

    const unresolvedParent =
      canonicalTreeFamilyUnitParentEvidenceBlocks(
        memberId,
        input.unresolvedEvidence,
      );

    const invalidParent =
      canonicalTreeFamilyUnitParentEvidenceBlocks(
        memberId,
        input.invalidEvidence,
      );

    let status:
      CanonicalTreeFamilyUnitIdentityStatus;

    let familyUnitId:
      string | null =
      null;

    if (invalidParent) {
      status =
        'BLOCKED_INVALID_PARENT';
    } else if (unresolvedParent) {
      status =
        'BLOCKED_UNRESOLVED_PARENT';
    } else if (
      canonicalParentMemberIds.length ===
      0
    ) {
      status =
        'UNCERTIFIED_ZERO_PARENT';
    } else {
      status =
        'CERTIFIED';

      familyUnitId =
        canonicalTreeFamilyUnitId(
          canonicalParentMemberIds,
        );
    }

    identityByMemberId.set(
      memberId,
      Object.freeze({
        memberId,
        status,
        familyUnitId,
        canonicalParentMemberIds,
      }),
    );
  }

  return Object.freeze({
    identityByMemberId,

    mutatesCanonicalTopology:
      false as const,

    mutatesCanonicalMembers:
      false as const,

    grantsMutationAuthority:
      false as const,
  });
};


/*
 * CC-TREE-LIVE-003G3T — parent-lineage signature
 *
 * Parent contribution tokens are explanatory presentation only.
 *
 * Parents in the same certified parent set conflict.
 *
 * In addition, alternate co-parents around one shared parent conflict:
 *
 *   A+B
 *   A+C
 *
 * B and C receive different tokens while A retains one stable token.
 */

export type CanonicalTreeParentLineageToken =
  | 'kc-tree-parent-lineage-v1'
  | 'kc-tree-parent-lineage-v2'
  | 'kc-tree-parent-lineage-v3'
  | 'kc-tree-parent-lineage-v4'
  | 'kc-tree-parent-lineage-v5'
  | 'kc-tree-parent-lineage-v6'
  | 'kc-tree-parent-lineage-v7'
  | 'kc-tree-parent-lineage-v8'
  | 'kc-tree-parent-lineage-v9'
  | 'kc-tree-parent-lineage-v10'
  | 'kc-tree-parent-lineage-v11'
  | 'kc-tree-parent-lineage-v12';

export const CANONICAL_TREE_PARENT_LINEAGE_TOKENS:
  readonly CanonicalTreeParentLineageToken[] =
  Object.freeze([
    'kc-tree-parent-lineage-v1',
    'kc-tree-parent-lineage-v2',
    'kc-tree-parent-lineage-v3',
    'kc-tree-parent-lineage-v4',
    'kc-tree-parent-lineage-v5',
    'kc-tree-parent-lineage-v6',
    'kc-tree-parent-lineage-v7',
    'kc-tree-parent-lineage-v8',
    'kc-tree-parent-lineage-v9',
    'kc-tree-parent-lineage-v10',
    'kc-tree-parent-lineage-v11',
    'kc-tree-parent-lineage-v12',
  ]);

export interface CanonicalTreeParentLineageSignatureInput {
  readonly identityProjection:
    CanonicalTreeFamilyUnitIdentityProjection;
}

export interface CanonicalTreeParentLineageSignature {
  readonly tokenByParentMemberId:
    ReadonlyMap<
      string,
      CanonicalTreeParentLineageToken
    >;

  readonly segmentTokensByMemberId:
    ReadonlyMap<
      string,
      readonly CanonicalTreeParentLineageToken[]
    >;

  readonly unallocatedParentMemberIds:
    readonly string[];

  readonly mutatesCanonicalTopology:
    false;

  readonly grantsMutationAuthority:
    false;
}

const addCanonicalTreeLineageConflict = (
  graph:
    Map<string, Set<string>>,
  left: string,
  right: string,
): void => {
  if (
    !left ||
    !right ||
    left === right
  ) {
    return;
  }

  if (!graph.has(left)) {
    graph.set(
      left,
      new Set<string>(),
    );
  }

  if (!graph.has(right)) {
    graph.set(
      right,
      new Set<string>(),
    );
  }

  graph
    .get(left)!
    .add(right);

  graph
    .get(right)!
    .add(left);
};

export const allocateCanonicalTreeParentLineageSignature = (
  input:
    CanonicalTreeParentLineageSignatureInput,
): CanonicalTreeParentLineageSignature => {
  const certifiedParentSets =
    Array.from(
      input
        .identityProjection
        .identityByMemberId
        .values(),
    )
      .filter(
        identity =>
          identity.status ===
            'CERTIFIED' &&
          identity
            .canonicalParentMemberIds
            .length > 0,
      )
      .map(
        identity =>
          Object.freeze([
            ...identity
              .canonicalParentMemberIds,
          ]),
      );

  const conflictGraph =
    new Map<
      string,
      Set<string>
    >();

  for (
    const parentSet of
    certifiedParentSets
  ) {
    for (const parentId of parentSet) {
      if (!conflictGraph.has(parentId)) {
        conflictGraph.set(
          parentId,
          new Set<string>(),
        );
      }
    }

    for (
      let left = 0;
      left < parentSet.length;
      left += 1
    ) {
      for (
        let right =
          left + 1;
        right <
          parentSet.length;
        right += 1
      ) {
        addCanonicalTreeLineageConflict(
          conflictGraph,
          parentSet[left],
          parentSet[right],
        );
      }
    }
  }

  /*
   * Alternate co-parent conflict.
   *
   * For every shared parent, all of that parent's
   * co-parents across certified family units form
   * a presentation conflict set.
   */
  for (
    const sharedParentId of
    conflictGraph.keys()
  ) {
    const alternateParents =
      Array.from(
        new Set(
          certifiedParentSets
            .filter(
              parentSet =>
                parentSet.includes(
                  sharedParentId,
                ),
            )
            .flatMap(
              parentSet =>
                parentSet.filter(
                  parentId =>
                    parentId !==
                    sharedParentId,
                ),
            ),
        ),
      ).sort();

    for (
      let left = 0;
      left <
        alternateParents.length;
      left += 1
    ) {
      for (
        let right =
          left + 1;
        right <
          alternateParents.length;
        right += 1
      ) {
        addCanonicalTreeLineageConflict(
          conflictGraph,
          alternateParents[left],
          alternateParents[right],
        );
      }
    }
  }

  const orderedParents =
    Array.from(
      conflictGraph.keys(),
    ).sort(
      (left, right) =>
        (
          conflictGraph
            .get(right)
            ?.size ??
          0
        ) -
          (
            conflictGraph
              .get(left)
              ?.size ??
            0
          ) ||
        left.localeCompare(right),
    );

  const tokenByParentMemberId =
    new Map<
      string,
      CanonicalTreeParentLineageToken
    >();

  const unallocatedParentMemberIds:
    string[] =
    [];

  for (const parentId of orderedParents) {
    const forbidden =
      new Set(
        Array.from(
          conflictGraph.get(
            parentId,
          ) ??
          new Set<string>(),
        )
          .map(
            neighborId =>
              tokenByParentMemberId.get(
                neighborId,
              ),
          )
          .filter(
            (
              token,
            ): token is
              CanonicalTreeParentLineageToken =>
              Boolean(token),
          ),
      );

    const selected =
      CANONICAL_TREE_PARENT_LINEAGE_TOKENS
        .find(
          token =>
            !forbidden.has(
              token,
            ),
        ) ??
      null;

    if (!selected) {
      unallocatedParentMemberIds.push(
        parentId,
      );
      continue;
    }

    tokenByParentMemberId.set(
      parentId,
      selected,
    );
  }

  const segmentTokensByMemberId =
    new Map<
      string,
      readonly CanonicalTreeParentLineageToken[]
    >();

  const orderedIdentities =
    Array.from(
      input
        .identityProjection
        .identityByMemberId
        .values(),
    ).sort(
      (left, right) =>
        left.memberId.localeCompare(
          right.memberId,
        ),
    );

  for (const identity of orderedIdentities) {
    if (
      identity.status !==
      'CERTIFIED'
    ) {
      segmentTokensByMemberId.set(
        identity.memberId,
        Object.freeze([]),
      );

      continue;
    }

    const tokens =
      identity
        .canonicalParentMemberIds
        .map(
          parentId =>
            tokenByParentMemberId.get(
              parentId,
            ),
        )
        .filter(
          (
            token,
          ): token is
            CanonicalTreeParentLineageToken =>
            Boolean(token),
        );

    segmentTokensByMemberId.set(
      identity.memberId,
      Object.freeze(tokens),
    );
  }

  return Object.freeze({
    tokenByParentMemberId,
    segmentTokensByMemberId,

    unallocatedParentMemberIds:
      Object.freeze([
        ...unallocatedParentMemberIds,
      ]),

    mutatesCanonicalTopology:
      false as const,

    grantsMutationAuthority:
      false as const,
  });
};


/*
 * CC-TREE-LIVE-003G3T — canonical family-unit palette
 *
 * This authority receives family identity plus ALREADY-ACCEPTED card
 * geometry. Geometry may determine presentation collision only.
 *
 * Geometry never determines:
 * - family identity;
 * - parentage;
 * - kinship;
 * - generation;
 * - topology.
 *
 * Twelve historical 003G3S base classes are reusable. They are not a
 * twelve-family ceiling.
 */

export type CanonicalTreeFamilyUnitVariantClass =
  | 'kc-tree-family-variant-v1'
  | 'kc-tree-family-variant-v2'
  | 'kc-tree-family-variant-v3'
  | 'kc-tree-family-variant-v4';

export const CANONICAL_TREE_FAMILY_UNIT_VARIANTS:
  readonly CanonicalTreeFamilyUnitVariantClass[] =
  Object.freeze([
    'kc-tree-family-variant-v1',
    'kc-tree-family-variant-v2',
    'kc-tree-family-variant-v3',
    'kc-tree-family-variant-v4',
  ]);

export interface CanonicalTreeFamilyUnitPaletteCandidate {
  readonly familyUnitId:
    string;

  readonly tier:
    number;

  readonly x:
    number;

  readonly width:
    number;
}

export interface CanonicalTreeFamilyUnitPaletteVisual {
  readonly baseClass:
    CanonicalTreeVisibleFamilyPaletteClass | null;

  readonly variantClass:
    CanonicalTreeFamilyUnitVariantClass | null;

  readonly failClosed:
    boolean;
}

export interface CanonicalTreeFamilyUnitPaletteInput {
  readonly candidates:
    readonly CanonicalTreeFamilyUnitPaletteCandidate[];
}

export interface CanonicalTreeFamilyUnitPaletteResult {
  readonly allocationByFamilyUnitId:
    ReadonlyMap<
      string,
      CanonicalTreeFamilyUnitPaletteVisual
    >;

  readonly adjacencyByFamilyUnitId:
    ReadonlyMap<
      string,
      ReadonlySet<string>
    >;

  readonly familyUnitCount:
    number;

  readonly baseColorCount:
    12;

  readonly variantCount:
    4;

  readonly unallocatedFamilyUnitIds:
    readonly string[];

  readonly mutatesCanonicalGeometry:
    false;

  readonly mutatesCanonicalTopology:
    false;

  readonly grantsMutationAuthority:
    false;
}

const canonicalTreeFamilyUnitStableHash = (
  value: string,
): number => {
  let hash =
    2166136261;

  for (const character of value) {
    hash ^=
      character.charCodeAt(0);

    hash =
      Math.imul(
        hash,
        16777619,
      );
  }

  return hash >>> 0;
};

const addCanonicalTreeFamilyUnitPaletteConflict = (
  graph:
    Map<string, Set<string>>,
  left: string,
  right: string,
): void => {
  if (
    !left ||
    !right ||
    left === right
  ) {
    return;
  }

  if (!graph.has(left)) {
    graph.set(
      left,
      new Set<string>(),
    );
  }

  if (!graph.has(right)) {
    graph.set(
      right,
      new Set<string>(),
    );
  }

  graph
    .get(left)!
    .add(right);

  graph
    .get(right)!
    .add(left);
};

export const allocateCanonicalTreeFamilyUnitPalette = (
  input:
    CanonicalTreeFamilyUnitPaletteInput,
): CanonicalTreeFamilyUnitPaletteResult => {
  const familyUnitIds =
    Array.from(
      new Set(
        input.candidates
          .map(
            candidate =>
              candidate.familyUnitId,
          )
          .filter(Boolean),
      ),
    ).sort();

  const conflictGraph =
    new Map<
      string,
      Set<string>
    >();

  for (const familyUnitId of familyUnitIds) {
    conflictGraph.set(
      familyUnitId,
      new Set<string>(),
    );
  }

  /*
   * Invalid geometry cannot safely participate in collision
   * allocation. Keep the family known but fail that family
   * closed rather than inventing adjacency.
   */
  const invalidGeometryFamilyUnitIds =
    new Set<string>();

  const validCandidates =
    input.candidates.filter(
      candidate => {
        const valid =
          Boolean(
            candidate.familyUnitId,
          ) &&
          Number.isFinite(
            candidate.tier,
          ) &&
          Number.isFinite(
            candidate.x,
          ) &&
          Number.isFinite(
            candidate.width,
          ) &&
          candidate.width > 0;

        if (
          !valid &&
          candidate.familyUnitId
        ) {
          invalidGeometryFamilyUnitIds.add(
            candidate.familyUnitId,
          );
        }

        return valid;
      },
    );

  const tiers =
    Array.from(
      new Set(
        validCandidates.map(
          candidate =>
            candidate.tier,
        ),
      ),
    ).sort(
      (left, right) =>
        left - right,
    );

  for (const tier of tiers) {
    const tierCandidates =
      validCandidates.filter(
        candidate =>
          candidate.tier ===
          tier,
      );

    const tierFamilyUnitIds =
      Array.from(
        new Set(
          tierCandidates.map(
            candidate =>
              candidate.familyUnitId,
          ),
        ),
      ).sort();

    const envelopes =
      tierFamilyUnitIds
        .map(
          familyUnitId => {
            const owned =
              tierCandidates.filter(
                candidate =>
                  candidate.familyUnitId ===
                  familyUnitId,
              );

            return Object.freeze({
              familyUnitId,

              minX:
                Math.min(
                  ...owned.map(
                    candidate =>
                      candidate.x -
                      candidate.width /
                        2,
                  ),
                ),

              maxX:
                Math.max(
                  ...owned.map(
                    candidate =>
                      candidate.x +
                      candidate.width /
                        2,
                  ),
                ),
            });
          },
        )
        .sort(
          (left, right) =>
            left.minX -
              right.minX ||
            left.maxX -
              right.maxX ||
            left.familyUnitId
              .localeCompare(
                right.familyUnitId,
              ),
        );

    /*
     * Every overlapping/interleaving family-unit envelope
     * conflicts, including non-consecutive sorted envelopes.
     */
    for (
      let left = 0;
      left < envelopes.length;
      left += 1
    ) {
      for (
        let right =
          left + 1;
        right <
          envelopes.length;
        right += 1
      ) {
        if (
          envelopes[right].minX <=
          envelopes[left].maxX
        ) {
          addCanonicalTreeFamilyUnitPaletteConflict(
            conflictGraph,
            envelopes[left]
              .familyUnitId,
            envelopes[right]
              .familyUnitId,
          );
        }
      }
    }

    /*
     * Consecutive left-to-right family units are side by side
     * even when the accepted layout leaves a visual gap.
     */
    for (
      let index = 0;
      index <
        envelopes.length - 1;
      index += 1
    ) {
      addCanonicalTreeFamilyUnitPaletteConflict(
        conflictGraph,
        envelopes[index]
          .familyUnitId,
        envelopes[index + 1]
          .familyUnitId,
      );
    }
  }

  /*
   * Deterministic greedy graph coloring:
   *
   * 1. highest conflict degree first;
   * 2. family-unit identity lexical tie-break;
   * 3. deterministic hashed preferred base;
   * 4. probe twelve reusable 003G3S base colors.
   */
  const orderedFamilyUnitIds =
    [...familyUnitIds]
      .sort(
        (left, right) =>
          (
            conflictGraph
              .get(right)
              ?.size ??
            0
          ) -
            (
              conflictGraph
                .get(left)
                ?.size ??
              0
            ) ||
          left.localeCompare(right),
      );

  const allocationByFamilyUnitId =
    new Map<
      string,
      CanonicalTreeFamilyUnitPaletteVisual
    >();

  const unallocatedFamilyUnitIds:
    string[] =
    [];

  for (
    const familyUnitId of
    orderedFamilyUnitIds
  ) {
    if (
      invalidGeometryFamilyUnitIds.has(
        familyUnitId,
      )
    ) {
      allocationByFamilyUnitId.set(
        familyUnitId,
        Object.freeze({
          baseClass:
            null,

          variantClass:
            null,

          failClosed:
            true,
        }),
      );

      unallocatedFamilyUnitIds.push(
        familyUnitId,
      );

      continue;
    }

    const forbiddenBaseClasses =
      new Set<
        CanonicalTreeVisibleFamilyPaletteClass
      >();

    for (
      const neighborId of
      conflictGraph.get(
        familyUnitId,
      ) ??
      new Set<string>()
    ) {
      const neighborBaseClass =
        allocationByFamilyUnitId
          .get(
            neighborId,
          )
          ?.baseClass;

      if (neighborBaseClass) {
        forbiddenBaseClasses.add(
          neighborBaseClass,
        );
      }
    }

    const preferredBaseIndex =
      canonicalTreeFamilyUnitStableHash(
        `base:${familyUnitId}`,
      ) %
      CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS
        .length;

    let selectedBaseClass:
      CanonicalTreeVisibleFamilyPaletteClass | null =
      null;

    for (
      let offset = 0;
      offset <
        CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS
          .length;
      offset += 1
    ) {
      const candidateBaseClass =
        CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS[
          (
            preferredBaseIndex +
            offset
          ) %
          CANONICAL_TREE_VISIBLE_FAMILY_VARIANTS
            .length
        ];

      if (
        !forbiddenBaseClasses.has(
          candidateBaseClass,
        )
      ) {
        selectedBaseClass =
          candidateBaseClass;

        break;
      }
    }

    if (!selectedBaseClass) {
      allocationByFamilyUnitId.set(
        familyUnitId,
        Object.freeze({
          baseClass:
            null,

          variantClass:
            null,

          failClosed:
            true,
        }),
      );

      unallocatedFamilyUnitIds.push(
        familyUnitId,
      );

      continue;
    }

    const variantClass =
      CANONICAL_TREE_FAMILY_UNIT_VARIANTS[
        canonicalTreeFamilyUnitStableHash(
          `variant:${familyUnitId}`,
        ) %
        CANONICAL_TREE_FAMILY_UNIT_VARIANTS
          .length
      ];

    allocationByFamilyUnitId.set(
      familyUnitId,
      Object.freeze({
        baseClass:
          selectedBaseClass,

        variantClass,

        failClosed:
          false,
      }),
    );
  }

  const adjacencyByFamilyUnitId =
    new Map<
      string,
      ReadonlySet<string>
    >();

  for (
    const familyUnitId of
    familyUnitIds
  ) {
    adjacencyByFamilyUnitId.set(
      familyUnitId,
      new Set(
        Array.from(
          conflictGraph.get(
            familyUnitId,
          ) ??
          new Set<string>(),
        ).sort(),
      ),
    );
  }

  return Object.freeze({
    allocationByFamilyUnitId,
    adjacencyByFamilyUnitId,

    familyUnitCount:
      familyUnitIds.length,

    baseColorCount:
      12 as const,

    variantCount:
      4 as const,

    unallocatedFamilyUnitIds:
      Object.freeze([
        ...unallocatedFamilyUnitIds,
      ].sort()),

    mutatesCanonicalGeometry:
      false as const,

    mutatesCanonicalTopology:
      false as const,

    grantsMutationAuthority:
      false as const,
  });
};
